import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as Cookies from 'universal-cookie';
import * as moment from 'moment';
import { IOrderRequest, IListItem } from '@nkr/models/index';
import { OrganType } from '@nkr/enums/index';
import useUpdateCompletionDeadline from '@nkr/store/phlebot-service/hooks/use-update-completion-deadline';
import { GridWrapper } from './styles';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, GridReadyEvent, SortChangedEvent } from 'ag-grid-community';
import { Button, Row, Col, Nav, NavItem, Modal } from 'react-bootstrap';
import TrackingSearchBar from './tracking-search-bar';
import { useOrderTrackingColumns } from './order-tracking-columns';
import { useOrderTrackingHandlers } from './order-tracking-handlers';
import { useOrderTrackingData } from './order-tracking-data';
import VendorPriorityModal from './vendor-priority-modal';
import { IVendorPriority } from './types';
import vendorPriorityService from './vendor-priority-service';
import { showSuccessToast, showErrorToast } from './utils/toast-helpers';

const getAuthHeaders = (): HeadersInit => {
    try {
        const cookies = new Cookies();
        const nkrAuth = cookies.get('NkrAuth');
        const token = nkrAuth?.token?.access_token as string | undefined;
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
};

const CANCELLED_STATUS_ID = 0; // KitOrderStatus.CANCELLED
const STREAMLINED_SEROLOGY_ENTITY_TYPE = 4; // NkrEntityType.STREAMLINED_SEROLOGY

interface ICancelModal {
    show: boolean;
    orderId: number | null;
    alias: string;
    sending: boolean;
    error: string | null;
}


interface IReorderModal {
    show: boolean;
    orderId: number | null;
    sending: boolean;
    error: string | null;
}

interface IFollowUpModal {
    show: boolean;
    orderId: number | null;
    sending: boolean;
    error: string | null;
}

interface OrderTrackingProps {
    isAdmin: boolean;
    centerId: number;
    organType: OrganType;
    list: IOrderRequest[];
    kitTypes: IListItem[];
    shipVendors: IListItem[];
    orderStatuses: IListItem[];
    onGridReady: (e: GridReadyEvent) => void;
    onDateSearch?: (startDate: string, endDate: string, dateType: number) => void;
    onOrderUpdated?: (order: IOrderRequest) => void;
    onCompletionDeadlineChanged?: (entityType: number, entityTypeId: number, completionDeadline: string) => void;
}

const OrderTracking: React.FC<OrderTrackingProps> = ({
    isAdmin,
    centerId,
    organType,
    list,
    kitTypes,
    shipVendors,
    orderStatuses,
    onGridReady,
    onDateSearch,
    onOrderUpdated,
    onCompletionDeadlineChanged
}) => {
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [activeTab, setActiveTab] = useState<'todo' | 'all'>('all');
    const [showVendorPriorityModal, setShowVendorPriorityModal] = useState(false);
    const [vendorPriorities, setVendorPriorities] = useState<IVendorPriority[]>([]);
    const [isLoadingPriorities, setIsLoadingPriorities] = useState(false);
    const [isSavingPriorities, setIsSavingPriorities] = useState(false);
    const gridWrapperRef = useRef<HTMLDivElement>(null);
    const [cancelModal, setCancelModal] = useState<ICancelModal>({
        show: false, orderId: null, alias: '', sending: false, error: null
    });
    const [reorderModal, setReorderModal] = useState<IReorderModal>({
        show: false, orderId: null, sending: false, error: null
    });
    const [followUpModal, setFollowUpModal] = useState<IFollowUpModal>({
        show: false, orderId: null, sending: false, error: null
    });

    const {
        filteredList,
        todoList,
        handleSearch,
        handleDateRangeSearch
    } = useOrderTrackingData(list, gridApi, activeTab, onDateSearch);

    const handleCancelAction = useCallback((id: number) => {
        const order = list.find(x => x.id === id);
        const alias = order?.entityType === STREAMLINED_SEROLOGY_ENTITY_TYPE
            ? order.donorAlias
            : order?.patientAlias || order?.donorAlias || order?.donorCenterAlias || '';
        setCancelModal({ show: true, orderId: id, alias, sending: false, error: null });
    }, [list]);

    const handleCancelConfirm = useCallback(async () => {
        if (!cancelModal.orderId) return;
        setCancelModal(prev => ({ ...prev, sending: true, error: null }));
        try {
            const resp = await fetch(`${__API__}/admin/orders/${cancelModal.orderId}/cancel`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ alias: cancelModal.alias })
            });
            const data = await resp.json();
            if (data.success) {
                const order = list.find(x => x.id === cancelModal.orderId);
                if (order && onOrderUpdated) {
                    onOrderUpdated({ ...order, statusId: CANCELLED_STATUS_ID });
                }
                setCancelModal({ show: false, orderId: null, alias: '', sending: false, error: null });
                showSuccessToast('Cancellation request sent to vendor.');
            } else {
                setCancelModal(prev => ({ ...prev, sending: false, error: data.message || 'Failed to send cancellation.' }));
            }
        } catch {
            setCancelModal(prev => ({ ...prev, sending: false, error: 'Request failed. Please try again.' }));
        }
    }, [cancelModal, list, onOrderUpdated]);

    const handleReorderAction = useCallback((id: number) => {
        setReorderModal({ show: true, orderId: id, sending: false, error: null });
    }, []);

    const handleReorderConfirm = useCallback(async () => {
        if (!reorderModal.orderId) return;
        setReorderModal(prev => ({ ...prev, sending: true, error: null }));
        try {
            const response = await fetch(`${__API__}/admin/orders/${reorderModal.orderId}/reorder`, { method: 'POST', headers: getAuthHeaders() });
            const data = await response.json();
            if (data.success) {
                setReorderModal({ show: false, orderId: null, sending: false, error: null });
                showSuccessToast('Order has been resubmitted to the vendor.');
            } else {
                setReorderModal(prev => ({ ...prev, sending: false, error: data.message || 'Reorder failed.' }));
            }
        } catch {
            setReorderModal(prev => ({ ...prev, sending: false, error: 'Request failed. Please try again.' }));
        }
    }, [reorderModal.orderId]);

    const handleFollowUpEmailAction = useCallback((id: number) => {
        setFollowUpModal({ show: true, orderId: id, sending: false, error: null });
    }, []);

    const handleFollowUpEmailConfirm = useCallback(async () => {
        if (!followUpModal.orderId) return;
        setFollowUpModal(prev => ({ ...prev, sending: true, error: null }));
        try {
            const resp = await fetch(`${__API__}/admin/orders/${followUpModal.orderId}/follow-up`, { method: 'POST', headers: getAuthHeaders() });
            const data = await resp.json();
            if (data.success) {
                setFollowUpModal({ show: false, orderId: null, sending: false, error: null });
                showSuccessToast('Follow-up email sent.');
            } else {
                setFollowUpModal(prev => ({ ...prev, sending: false, error: data.message || 'Failed to send follow-up email.' }));
            }
        } catch {
            setFollowUpModal(prev => ({ ...prev, sending: false, error: 'Request failed. Please try again.' }));
        }
    }, [followUpModal.orderId]);

    const { error: completionDeadlineError, update: updateCompletionDeadline } = useUpdateCompletionDeadline();

    useEffect(() => {
        if (completionDeadlineError) {
            showErrorToast(completionDeadlineError);
        }
    }, [completionDeadlineError]);

    const handleCompletionDeadlineSelected = useCallback(async (id: number, deadline: string) => {
        const order = list.find(x => x.id === id);

        if (await updateCompletionDeadline(id, deadline)) {
            const completionDeadline = deadline ? moment(deadline, 'MM/DD/YYYY').format('YYYY-MM-DDT23:59:59') : null;
            onCompletionDeadlineChanged && onCompletionDeadlineChanged(order.entityType, order.entityTypeId, completionDeadline);
            showSuccessToast(deadline ? 'Collection deadline updated.' : 'Collection deadline reset to default.');
        }
    }, [list, onCompletionDeadlineChanged]);

    const {
        handleTabSelect,
        handleGridReady,
        onGridSizeChanged,
        onDataFirstRendered,
        csvExport,
        onRowDataChanged
    } = useOrderTrackingHandlers(
        todoList,
        filteredList,
        kitTypes,
        shipVendors,
        gridApi,
        setActiveTab,
        setGridApi,
        onGridReady,
    );

    const columns = useOrderTrackingColumns(isAdmin, centerId, kitTypes, shipVendors, orderStatuses, organType, handleCancelAction, handleReorderAction, handleFollowUpEmailAction, handleCompletionDeadlineSelected);

    const sizeGrid = useCallback(() => {
        if (gridApi && gridWrapperRef.current) {
            const gridElement = gridWrapperRef.current.querySelector('.ag-root-wrapper');
            if (gridElement) {
                gridApi.sizeColumnsToFit();
                // Firefox defers layout flush; second call after two frames lets scrollWidth settle
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        gridApi.sizeColumnsToFit();
                    });
                });
            }
        }
    }, [gridApi]);

    useEffect(() => {
        window.addEventListener('resize', sizeGrid);
        return () => {
            window.removeEventListener('resize', sizeGrid);
        };
    }, [sizeGrid]);

    useEffect(() => {
        if (gridApi) {
            requestAnimationFrame(() => {
                setTimeout(sizeGrid, 100);
            });
        }
    }, [gridApi, activeTab, filteredList, todoList, sizeGrid]);

    const loadVendorPriorities = useCallback(async () => {
        try {
            setIsLoadingPriorities(true);
            const priorities = await vendorPriorityService.getVendorPriorities();
            setVendorPriorities(priorities);
        } catch (error) {
            console.error('Failed to load vendor priorities:', error);
        } finally {
            setIsLoadingPriorities(false);
        }
    }, []);

    const handleOpenVendorPriorityModal = useCallback(() => {
        loadVendorPriorities();
        setShowVendorPriorityModal(true);
    }, [loadVendorPriorities]);

    const handleSaveVendorPriorities = useCallback(async (updatedPriorities: IVendorPriority[]) => {
        try {
            setIsSavingPriorities(true);
            setVendorPriorities(updatedPriorities);
            return Promise.resolve();
        } catch (error) {
            console.error('Failed to save vendor priorities:', error);
            return Promise.reject(error);
        } finally {
            setIsSavingPriorities(false);
        }
    }, []);

    const onGridReadyWrapper = useCallback((params: GridReadyEvent) => {
        setGridApi(params.api);
        onGridReady(params);
        setTimeout(sizeGrid, 0);
        setTimeout(sizeGrid, 500);
    }, [onGridReady, sizeGrid]);

    const onSortChanged = useCallback((e: SortChangedEvent) => {
        e.api.refreshCells();
    }, []);

    return (
        <>
            <div style={{ display: 'flex', paddingLeft: 'inherit', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <h3 style={{ margin: 0 }}>Order Tracking Report</h3>
                <div style={{ float: 'right', marginRight: '25px', marginTop: '10px' }}>
                    <Button onClick={csvExport} style={{ marginRight: '10px' }}>Export CSV</Button>
                    {isAdmin && (
                        <Button onClick={handleOpenVendorPriorityModal} bsStyle="primary">
                            Vendor Distribution Management
                        </Button>
                    )}
                </div>
            </div>

            <TrackingSearchBar
                organType={organType}
                kitTypes={kitTypes}
                shipVendors={shipVendors}
                orderStatuses={orderStatuses}
                onSearch={handleSearch}
                onDateChange={handleDateRangeSearch}
            />

            <Row>
                <Col xs={12}>
                    <Nav bsStyle="tabs" style={{ paddingLeft: 'inherit' }} activeKey={activeTab} onSelect={(key: any) => handleTabSelect(key)}>
                        {/* <NavItem eventKey="todo">To Do ({todoList.length})</NavItem> */}
                        <NavItem eventKey="all">All Orders ({filteredList.length})</NavItem>
                    </Nav>
                </Col>
            </Row>

            <GridWrapper ref={gridWrapperRef} id="grid-wrapper" className='container-fluid ag-theme-bootstrap'>
                <AgGridReact
                    onGridReady={onGridReadyWrapper}
                    onGridSizeChanged={sizeGrid}
                    onFirstDataRendered={sizeGrid}
                    onSortChanged={onSortChanged}
                    enableCellTextSelection={true}
                    rowData={activeTab === 'todo' ? todoList : filteredList}
                    immutableData={true}
                    getRowNodeId={(data: IOrderRequest) => data.id.toString()}
                    headerHeight={45}
                    rowHeight={40}
                    enableCellChangeFlash={true}
                    suppressCellSelection={true}
                    rowStyle={{ overflow: 'visible' }}
                    columnDefs={columns}
                    noRowsOverlayComponentFramework={() => <div>No Orders found...</div>}
                    onRowDataChanged={onRowDataChanged}
                />

                <div style={{ marginBottom: 5 }}>
                    {activeTab === 'todo' ?
                        `${todoList.length} items to do` :
                        `${filteredList.length} of ${list.length} records shown...`
                    }
                </div>
            </GridWrapper>

            {/* Vendor Priority Modal */}
            <VendorPriorityModal
                show={showVendorPriorityModal}
                vendorPriorities={vendorPriorities}
                shipVendors={shipVendors}
                isLoading={isLoadingPriorities || isSavingPriorities}
                onCancel={() => setShowVendorPriorityModal(false)}
                onSave={handleSaveVendorPriorities}
            />

            {/* Cancel Order Modal */}
            <Modal show={cancelModal.show} onHide={() => setCancelModal(prev => ({ ...prev, show: false }))}>
                <Modal.Header closeButton>
                    <Modal.Title>Cancel Order</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {cancelModal.error && <p style={{ color: 'red' }}>{cancelModal.error}</p>}
                    <p>A cancellation request will be sent to the vendor. This will update the order status to <strong>Cancelled</strong> and clear tracking data.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setCancelModal(prev => ({ ...prev, show: false }))} disabled={cancelModal.sending}>Close</Button>
                    <Button bsStyle="danger" onClick={handleCancelConfirm} disabled={cancelModal.sending}>
                        {cancelModal.sending ? 'Sending...' : 'Send Cancellation'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Reorder Modal */}
            <Modal show={reorderModal.show} onHide={() => setReorderModal(prev => ({ ...prev, show: false }))}>
                <Modal.Header closeButton>
                    <Modal.Title>Reorder Kit</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {reorderModal.error && <p style={{ color: 'red' }}>{reorderModal.error}</p>}
                    <p>This will place a new order through the same vendor. A confirmation email will be sent and the original order's data will remain unchanged.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setReorderModal(prev => ({ ...prev, show: false }))} disabled={reorderModal.sending}>Close</Button>
                    <Button bsStyle="primary" onClick={handleReorderConfirm} disabled={reorderModal.sending}>
                        {reorderModal.sending ? 'Submitting...' : 'Confirm Reorder'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Follow-up Email Modal */}
            <Modal show={followUpModal.show} onHide={() => setFollowUpModal(prev => ({ ...prev, show: false }))}>
                <Modal.Header closeButton>
                    <Modal.Title>Send Follow-up Email</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {followUpModal.error && <p style={{ color: 'red' }}>{followUpModal.error}</p>}
                    <p>This will send a follow-up email to confirm sample arrival based on this kit type. The email will include the return tracking number and be CC'd to NKR Operations.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setFollowUpModal(prev => ({ ...prev, show: false }))} disabled={followUpModal.sending}>Close</Button>
                    <Button bsStyle="primary" onClick={handleFollowUpEmailConfirm} disabled={followUpModal.sending}>
                        {followUpModal.sending ? 'Sending...' : 'Send Follow-up Email'}
                    </Button>
                </Modal.Footer>
            </Modal>

        </>
    );
};

export default OrderTracking;